import React, { useState } from 'react';
import { Modal, DatePicker, Button, Radio, message, Alert } from 'antd';
import { Database, Calendar, MapPin } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

interface DatabaseDataSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (params: {
    startDate: string;
    endDate: string;
    aggregationLevel: 'city' | 'state' | 'dma';
  }) => void;
  isLoading?: boolean;
}

const DatabaseDataSelector: React.FC<DatabaseDataSelectorProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false
}) => {
  // Default to 52 weeks of data
  const defaultEndDate = dayjs();
  const defaultStartDate = defaultEndDate.subtract(52, 'weeks');

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    defaultStartDate,
    defaultEndDate
  ]);
  const [aggregationLevel, setAggregationLevel] = useState<'city' | 'state' | 'dma'>('city');

  const handleConfirm = () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.error('Please select a date range');
      return;
    }

    const weeksDiff = dateRange[1].diff(dateRange[0], 'weeks');
    if (weeksDiff < 1) {
      message.error('Date range must be at least 1 week');
      return;
    }

    onConfirm({
      startDate: dateRange[0].format('YYYY-MM-DD'),
      endDate: dateRange[1].format('YYYY-MM-DD'),
      aggregationLevel
    });
  };

  const disabledDate = (current: Dayjs) => {
    return current && current > dayjs().endOf('day');
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([dates[0], dates[1]]);
    }
  };

  const weeksDiff = dateRange[1].diff(dateRange[0], 'weeks');

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-indigo-600" />
          <span className="text-lg font-semibold">Load Database Data</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          loading={isLoading}
        >
          Load Data
        </Button>
      ]}
      width={600}
      centered
    >
      <div className="space-y-6 mt-4">
        {/* Info Alert */}
        <Alert
          message="Database Data Source"
          description="This will load aggregated transaction data from your database, automatically processed by location and date for Geo-Lift analysis."
          type="info"
          showIcon
        />

        {/* Date Range Selection */}
        <div>
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
            <Calendar className="w-4 h-4 text-indigo-600" />
            Date Range
            <span className="text-red-500">*</span>
          </label>
          <RangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
            disabledDate={disabledDate}
            size="large"
            className="w-full"
            format="YYYY-MM-DD"
            placeholder={['Start Date', 'End Date']}
          />
          <p className="text-xs text-gray-500 mt-2">
            Selected range: <strong>{weeksDiff} weeks</strong> (Default: 52 weeks recommended for Geo-Lift)
          </p>
        </div>

        {/* Aggregation Level */}
        <div>
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
            <MapPin className="w-4 h-4 text-indigo-600" />
            Aggregation Level
            <span className="text-red-500">*</span>
          </label>
          <Radio.Group
            value={aggregationLevel}
            onChange={(e) => setAggregationLevel(e.target.value)}
            className="w-full"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  aggregationLevel === 'city'
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 bg-white hover:border-indigo-300'
                }`}
              >
                <Radio value="city" className="w-full">
                  <div className="ml-2">
                    <p className="font-semibold text-gray-800">City Level</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Aggregate by "City, State" format
                    </p>
                  </div>
                </Radio>
              </div>

              <div
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  aggregationLevel === 'state'
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 bg-white hover:border-indigo-300'
                }`}
              >
                <Radio value="state" className="w-full">
                  <div className="ml-2">
                    <p className="font-semibold text-gray-800">State Level</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Aggregate by state only
                    </p>
                  </div>
                </Radio>
              </div>

              <div
                className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
                  aggregationLevel === 'dma'
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 bg-white hover:border-indigo-300'
                }`}
              >
                <Radio value="dma" className="w-full">
                  <div className="ml-2">
                    <p className="font-semibold text-gray-800">DMA Level</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Aggregate by Designated Market Area
                    </p>
                  </div>
                </Radio>
              </div>
            </div>
          </Radio.Group>
        </div>

        {/* Summary */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
          <p className="text-sm text-gray-700">
            <strong>Summary:</strong> Loading {weeksDiff} weeks of data from{' '}
            <strong>{dateRange[0].format('MMM D, YYYY')}</strong> to{' '}
            <strong>{dateRange[1].format('MMM D, YYYY')}</strong>, aggregated at the{' '}
            <strong>{aggregationLevel}</strong> level.
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default DatabaseDataSelector;
